import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence
} from 'firebase/auth';
import { doc, setDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { auth, db, collections } from '@/lib/firebase';
import { mapSelectionToIds } from '@/services/mappingService';
import { getAutomationById } from '@/services/automationService';
import { getPaymentUrl } from '@/services/paymentService';
import { toast } from 'react-hot-toast';

export const AuthContext = createContext<AuthContextType | null>(null);

function capitalizeWords(str: string): string {
  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  signUp: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    company: string,
    jobTitle: string
  ) => Promise<void>;
  signIn: (
    email: string,
    password: string,
    rememberMe: boolean
  ) => Promise<void>;
  logout: () => Promise<void>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Don't render children until initial auth state is determined
  // This prevents flashing of protected content before redirect
  async function signUp(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    company: string,
    jobTitle: string
  ) {
    if (!email || !password || !firstName || !lastName || !company || !jobTitle) {
      throw new Error('All fields are required');
    }

    try {
      const selectedObjectiveText = sessionStorage.getItem('selectedObjectiveText');
      const selectedPlanText = sessionStorage.getItem('selectedPlanText');

      if (!selectedObjectiveText || !selectedPlanText) {
        throw new Error('Please select an objective and plan before registering');
      }

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const { objectiveId, planId } = await mapSelectionToIds(
        selectedObjectiveText,
        selectedPlanText
      );


      const userRef = doc(db, collections.users, user.uid);
      
      // Check if the selected plan is the free plan (plan-0)
      const isFreeplan = planId === 'plan-0';
      
      // Create user document with appropriate values based on plan
      await setDoc(userRef, {
        firstName: capitalizeWords(firstName.trim()),
        lastName: capitalizeWords(lastName.trim()),
        company,
        jobTitle,
        email,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        selfAssessmentCompleted: false,
        selfAssessment: '',
        selfAssessmentCompletedAt: null,
        discAssessment: '',
        discAssessmentCompleted: false,
        evaluatorsNominated: false,
        evaluatorsCount: 0,
        selectedPlanId: planId,
        reportAvailable: false,
        accountApproved: true,
        evaluatorsFinalized: false,
        onboardingCompleted: false,
        planPaid: isFreeplan // Set to true for free plan, false otherwise
      });

      // Get automation URL for new user webhook
      const automation = await getAutomationById('new-user');
      if (!automation?.url) {
        console.warn('New user webhook not configured');
      } else {
        try {
          const webhookPayload = {
            userId: user.uid,
            email: email.toLowerCase().trim(),
            firstName: capitalizeWords(firstName.trim()),
            lastName: capitalizeWords(lastName.trim()),
            company: company.trim(),
            jobTitle: jobTitle.trim(),
            objectiveId,
            planId,
            timestamp: new Date().toISOString(),
            environment: import.meta.env.MODE || 'development'
          };

          const response = await fetch(automation.url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify(webhookPayload)
          });

          if (!response.ok) {
            throw new Error(`Webhook failed with status: ${response.status}`);
          }
        } catch (error) {
          console.error('Failed to send new user webhook:', error);
          // Don't throw to avoid blocking signup process
        }
      }

      sessionStorage.removeItem('selectedObjectiveText');
      sessionStorage.removeItem('selectedPlanText');

      // For free plan, keep user signed in and return credential
      // For paid plans, get payment URL and redirect
      if (isFreeplan) {
        return userCredential;
      } else {
        // Get payment URL for paid plans
        const paymentUrl = await getPaymentUrl(planId, {
          email: email.toLowerCase().trim(),
          firstName: capitalizeWords(firstName.trim()),
          lastName: capitalizeWords(lastName.trim())
        });

        if (!paymentUrl) {
          await user.delete();
          throw new Error('Payment configuration not found');
        }

        // Sign out and redirect to payment for paid plans
        await signOut(auth);
        window.location.href = paymentUrl;
        return userCredential;
      }
    } catch (error: any) {
      console.error('Error during sign up:', error);
      
      switch (error.code) {
        case 'auth/email-already-in-use':
          throw new Error('This email is already registered');
        case 'auth/invalid-email':
          throw new Error('Please enter a valid email address');
        case 'auth/weak-password':
          throw new Error('Password should be at least 6 characters long');
        case 'auth/network-request-failed':
          throw new Error('Network error. Please check your internet connection.');
        default:
          if (error.message) {
            throw error;
          }
          throw new Error('Failed to create account. Please try again.');
      }
    }
  }

  async function signIn(email: string, password: string, rememberMe: boolean) {
    if (!email || !password) {
      throw new Error('Email and password are required');
    }

    try {
      // Set persistence based on rememberMe
      await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      // Check account approval status and sign out if not approved
      const userDoc = await getDoc(doc(db, collections.users, user.uid));
      if (!userDoc.exists() || !userDoc.data().accountApproved) {
        // Sign out immediately
        await signOut(auth);
        throw new Error('APPROVAL_PENDING');
      }
      
      const userData = userDoc.data(); // Retrieve user data for payment status check

      // Check payment status
      if (!userData.planPaid) {
        const paymentUrl = await getPaymentUrl(userData.selectedPlanId, {
          email: user.email || '',
          firstName: userData.firstName,
          lastName: userData.lastName
        });
        
        if (!paymentUrl) {
          throw new Error('Payment configuration not found');
        }

        // Sign out and redirect to payment
        await signOut(auth);
        window.location.href = paymentUrl;
        return; // Return early instead of throwing error
      }
      
      return userCredential;
    } catch (error: any) {
      console.error('Error during sign in:', error);
      
      // If there was an error, ensure user is signed out
      await signOut(auth);
      
      switch (error.code) {
        case 'auth/invalid-credential':
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          throw new Error('Invalid email or password');
        case 'auth/too-many-requests':
          throw new Error('Too many failed attempts. Please try again later.');
        case 'auth/network-request-failed':
          throw new Error('Network error. Please check your internet connection.');
        default:
          if (error.message === 'APPROVAL_PENDING') {
            throw new Error('APPROVAL_PENDING');
          }
          throw new Error('Failed to sign in. Please try again.');
      }
    }
  }

  async function logout() {
    try {
      await signOut(auth);
      sessionStorage.clear();
      localStorage.clear();
    } catch (error: any) {
      console.error('Error during logout:', error);
      throw new Error('Failed to log out. Please try again.');
    }
  }

  const value = {
    currentUser,
    loading,
    signUp,
    signIn,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
